/**
 * Categories API Service
 * 
 * All categories (niches and subcategories) API endpoints
 * following the API v2.0 specification.
 */

import { get, post } from '../../utils/httpClient';
import logger from '../../utils/logger';

// ============ NICHES ============

/**
 * Get all niches
 */
export const fetchAllNiches = async () => {
  try {
    const response = await get('/categories/niche/list');
    return response.data || [];
  } catch (error) {
    logger.error('Failed to fetch all niches', error);
    throw error;
  }
};

/**
 * Add a new niche
 */
export const addNiche = async (nicheData) => {
  try {
    const { name, image } = nicheData;
    if (!name || !image) {
      throw new Error('Name and image are required');
    }

    const response = await post('/categories/niche/add', {
      name: name.trim(),
      image: image.trim(),
    });
    return response;
  } catch (error) {
    logger.error('Failed to add niche', error, { nicheData });
    throw error;
  }
};

/**
 * Update a niche
 */
export const updateNiche = async (nicheData) => {
  try {
    const { id, name, image } = nicheData;
    if (!id) {
      throw new Error('Niche ID is required');
    }

    const payload = { id };
    if (name) payload.name = name.trim();
    if (image) payload.image = image.trim();

    const response = await post('/categories/niche/update', payload);
    return response;
  } catch (error) {
    logger.error('Failed to update niche', error, { nicheData });
    throw error;
  }
};

// ============ SUBCATEGORIES ============

/**
 * Get all subcategories
 */
export const fetchAllSubcategories = async () => {
  try {
    const response = await get('/categories/subcategory/list');
    return response.data || [];
  } catch (error) {
    logger.error('Failed to fetch all subcategories', error);
    throw error;
  }
};

/**
 * Get subcategories for a specific niche
 */
export const fetchSubcategoriesByNiche = async (nicheId) => {
  try {
    if (!nicheId) {
      throw new Error('Niche ID is required');
    }
    
    // Note: API uses "nice_id" instead of "niche_id" (typo in API)
    const response = await post('/categories/subcategory/list', {
      nice_id: nicheId,
    });
    
    // Note: API returns this in "plans" key (bug in API)
    return response.plans || response.data || [];
  } catch (error) {
    logger.error('Failed to fetch subcategories by niche', error, { nicheId });
    throw error;
  }
};

/**
 * Add a new subcategory
 */
export const addSubcategory = async (subcategoryData) => {
  try {
    const { niche_id, name, image } = subcategoryData;
    if (!niche_id || !name || !image) {
      throw new Error('Niche ID, name, and image are required');
    }

    const response = await post('/categories/subcategory/add', {
      niche_id,
      name: name.trim(),
      image: image.trim(),
    });
    return response;
  } catch (error) {
    logger.error('Failed to add subcategory', error, { subcategoryData });
    throw error;
  }
};

/**
 * Update a subcategory
 */
export const updateSubcategory = async (subcategoryData) => {
  try {
    const { id, name, image } = subcategoryData;
    if (!id) {
      throw new Error('Subcategory ID is required');
    }

    const payload = { id };
    if (name) payload.name = name.trim();
    if (image) payload.image = image.trim();

    const response = await post('/categories/subcategory/update', payload);
    return response;
  } catch (error) {
    logger.error('Failed to update subcategory', error, { subcategoryData });
    throw error;
  }
};

